import { useCallback } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { useTranslation } from 'react-i18next'

import { DownloadModal } from '~/pages/AdminUploads/components/DownloadModal'
import { useExportDemandGroups } from '~/pages/AdminUploads/hooks/useExportDemandGroups'
import type { DemandGroupCsvExportRow } from '~/pages/AdminUploads/models/demandGroupCsvExport'
import {
  buildDemandGroupCsvExport,
  demandGroupCsvExportFileName,
  toDemandGroupCsvExportRows,
} from '~/pages/AdminUploads/models/demandGroupCsvExport'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 入稿済み需要グループのダウンロード確認モーダル
 * (uploads/common.md §6 / AC-DUC-028 / AC-DUC-032)。
 */
export const DemandGroupDownloadModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 英語表示ではファイル名・ヘッダー行を英語で出す (common.md §6、demand-groups.md §1)。
  const isEnglish = i18n.language.startsWith('en')
  const { mutateAsync: exportAsync } = useExportDemandGroups()
  const typeName = t('page:adminUploads.types.demandGroups.name')

  const exportRows = useCallback(async () => {
    const resp = await exportAsync({})
    return {
      rows: toDemandGroupCsvExportRows(resp.rows),
      dataUpdatedAt:
        resp.dataUpdatedAt !== undefined
          ? timestampDate(resp.dataUpdatedAt)
          : undefined,
    }
  }, [exportAsync])

  const buildCsv = useCallback(
    (rows: DemandGroupCsvExportRow[]) =>
      buildDemandGroupCsvExport(rows, isEnglish),
    [isEnglish],
  )

  const fileName = useCallback(
    () => demandGroupCsvExportFileName(isEnglish),
    [isEnglish],
  )

  return (
    <DownloadModal<DemandGroupCsvExportRow>
      buildCsv={buildCsv}
      exportRows={exportRows}
      fileName={fileName}
      isOpen={isOpen}
      onClose={onClose}
      typeName={typeName}
    />
  )
}
